import React from 'react';
import { jsonLdScriptProps } from 'react-schemaorg';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import configJSON from 'config.json';
import packageJSON from 'package.json';
import { useThemeContext } from 'contexts/theme';
import { ThemeNames } from 'types/themes';
import { generateCanonicalUrl } from 'utils/urls/canonicalUrls';
import { HrefLangLink } from 'components/pages/area-listings/util/head-tags-builder';
import { isTenantExpCommercial } from 'utils/tenant/which-tenant';
import { getTenantById, themeNameToTenantId } from '@tenants';
import { getTenantHeadTagsConfig, getTenantOrganizationConfig } from '@tenants/utils/configs';
import { SchemaBuilder } from 'components/pages/area-listings/util/head-tags-builder/types/schema-builder';
import { useLocale } from 'hooks/use-locale';
import { resolveSchemaLocale } from 'utils/schema/schema-locale-resolver';


export type HeadDataType = {
  /** Plain string title (takes priority over i18nTitleKey) */
  title?: string;
  /** Translation key for title (used if title is not provided) */
  i18nTitleKey?: string;
  /** Plain string meta description (takes priority over i18nMetaDescriptionKey) */
  metaDescription?: string;
  /** Translation key for meta description (used if metaDescription is not provided) */
  i18nMetaDescriptionKey?: string;
  metaKeywords?: string;
  noIndex?: boolean;
  noFollow?: boolean;
  canonicalUrl?: string;
  placePostalCode?: string;
  placeStreetAddress?: string;
  placeLocality?: string;
  placeRegion?: string;
  ogImage?: string;
  ogType?: string;
  ogUrl?: string;
  schema?: Record<string, unknown>[];
  hrefLangs?: HrefLangLink[];
};

export interface Props {
  data?: HeadDataType;
}

export default function HeadData({ data }: Props) {
  const { asPath } = useRouter();
  const { t } = useTranslation();
  const { theme, themeName } = useThemeContext();
  const tenant = getTenantById(themeNameToTenantId(themeName));
  const organizationConfig = getTenantOrganizationConfig(tenant);
  const headTagsConfig = getTenantHeadTagsConfig(tenant);
  const isZoocasaTenant = themeName === ThemeNames.ZOOCASA;
  const router = useRouter();
  const { currentLocale } = useLocale(tenant.id);
  
  // Resolve locale for schemas based on tenant SEO config
  const localeForSchema = resolveSchemaLocale(tenant.id, currentLocale);
  
  // We want to add noindex to the search page for SEO purposes
  const isSearchPage = router.pathname && router.pathname.startsWith('/search');
  // We want to add noindex to all URLs with locale prefixes (non-default locales)
  // Use currentLocale (resolved via LocaleManager) instead of router.locale to handle
  // the mismatch between Next.js global default ('en') and tenant default ('en-CA')
  const hasLocalePrefix = currentLocale !== tenant.localization.defaultLanguage;

  const canonicalUrl = generateCanonicalUrl({
    asPath,
    baseHost: theme.schemaUrl,
    locale: router.locale || tenant.localization.defaultLanguage,
    tenantId: tenant.id,
  });

  // Resolve title: plain string > i18n key from data > tenant default (plain string)
  const resolvedTitle = data?.title 
    ?? (data?.i18nTitleKey ? t(data.i18nTitleKey) : headTagsConfig.defaultTitle);
  
  // Resolve meta description: plain string > i18n key from data > tenant default key (translated)
  const resolvedMetaDescription = data?.metaDescription 
    ?? (data?.i18nMetaDescriptionKey ? t(data.i18nMetaDescriptionKey) : t(headTagsConfig.defaultMetaDescriptionKey));

  const defaultMetaKeywords = 'mls listings, house for sale, mls canada, homes for rent, mls map, mls real estate, homes for sale, condos for sale, homes for sale near me, townhouse for sale, property for sale, apartments for sale, property search, mls online, house prices, duplex for sale, real estate listings, real estate companies, cheap houses for sale, mls house listings, best real estate websites canada, single family homes for sale, mls residential map, detached house for sale, home listings, sold home prices, property listings, sold mls listings, house sale prices';

  const properties = {
    iOSBannerHidden: false,
    title: resolvedTitle,
    metaDescription: resolvedMetaDescription,
    metaKeywords: data?.metaKeywords ?? defaultMetaKeywords,
    noIndex: data?.noIndex ?? (isSearchPage || hasLocalePrefix),
    noFollow: data?.noFollow ?? false,
    canonicalUrl: data?.canonicalUrl ?? canonicalUrl,
    placePostalCode: data?.placePostalCode ?? null,
    placeStreetAddress: data?.placeStreetAddress ?? null,
    placeLocality: data?.placeLocality ?? null,
    placeRegion: data?.placeRegion ?? null,
    ogSiteName: theme.ogSiteName,
    ogImage: data?.ogImage ?? theme.ogImage,
    ogImageAlt: theme.ogImageAlt,
    ogType: data?.ogType ?? 'website',
    ogUrl: data?.ogUrl ?? theme.schemaUrl,
    schema: data?.schema,
    hrefLangs: data?.hrefLangs,
  };

  const websiteSchema = new SchemaBuilder()
    .setContext('http://schema.org')
    .setType('WebSite')
    .setProperties({
      name: theme.name,
      description: properties.metaDescription,
      keywords: properties.metaKeywords,
      url: theme.schemaUrl,
    })
    .setInLanguage(localeForSchema)
    .build();

  const corporationSchema = new SchemaBuilder()
    .setContext('http://schema.org')
    .setType('Corporation')
    .setProperties({
      name: theme.name,
      description: properties.metaDescription,
      url: theme.schemaUrl,
      logo: theme.schemaLogo,
      location: {
        '@context': 'http://schema.org',
        '@type': 'PostalAddress',
        ...theme.schemaLocation,
      },
      foundingDate: theme.schemaFoundingDate,
      founders: theme.schemaFounders,
      telephone: organizationConfig.phoneNumber(),
      email: organizationConfig.email(),
      sameAs: [
        organizationConfig.twitterUrl,
        organizationConfig.facebookUrl,
        organizationConfig.instagramUrl,
        organizationConfig.linkedinUrl,
        themeName === ThemeNames.ZOOCASA && 'https://www.bbb.org/ca/on/toronto/profile/real-estate-services/zoocasa-realty-inc-0107-1380462',
      ],
    })
    .setInLanguage(localeForSchema)
    .build();

  // Add inLanguage to custom schemas from props
  const enrichedSchemas = (properties.schema || []).map(s => 
    new SchemaBuilder(s).setInLanguage(localeForSchema).build()
  );
  const schema = [websiteSchema, corporationSchema, ...enrichedSchemas];
  const urlPath = useRouter().asPath;
  const isJoinExpPage = urlPath === '/join-exp-us' || urlPath === '/join-exp-ca';
  const isExpCommercialHomepage = urlPath === '/' && isTenantExpCommercial(themeName);
  // Prevent Safari from automatically zooming in on text fields during user input without disabling the user's ability to pinch zoom.
  // See https://stackoverflow.com/a/57527009 for more info
  const isIOS = (): boolean =>
    typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);
  const viewPortContent = isIOS()
    ? 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=yes'
    : 'width=device-width, initial-scale=1.0, maximum-scale=5.0, user-scalable=yes';

  // Determine robots directive
  let robotsContent = '';
  if (properties.noIndex) robotsContent += 'noindex';
  if (properties.noFollow) {
    if (robotsContent) robotsContent += ',';
    robotsContent += 'nofollow';
  }

  return (
    <Head>
      {/* Title Tag */}
      <title>{properties.title}</title>

      {/* Meta Description Tag */}
      <meta name="description" content={properties.metaDescription} />

      {/* Meta Keywords Tag */}
      <meta name="keywords" content={properties.metaKeywords} />

      {/* Meta Viewport Tag */}
      <meta name="viewport" content={viewPortContent} />

      {/* Zoocasa Custom Tags */}
      <meta property="zoocasa:version" content={packageJSON.version} />
      {/* <meta property="zoocasa:version-git-sha" content={gitSha} /> */}

      {/* SEO Tags */}
      {properties.canonicalUrl && <link rel="canonical" href={properties.canonicalUrl} />}
      {(configJSON.host === 'https://stage.zoocasa.com' || /\.t\.zoocasa\.com$/.test(configJSON.host)) && <meta name="robots" content="noindex" />}
      {robotsContent && <meta name="robots" content={robotsContent} />}
      {/* Place Tags */}
      {properties.placePostalCode && <meta property="place:postal_code" content={properties.placePostalCode} />}
      {properties.placeStreetAddress && <meta property="place:street_address" content={properties.placeStreetAddress} />}
      {properties.placeLocality && <meta property="place:locality" content={properties.placeLocality} />}
      {properties.placeRegion && <meta property="place:region" content={properties.placeRegion} />}

      {/* Open Graph Tags */}
      {properties.ogSiteName && <meta property="og:site_name" content={properties.ogSiteName} />}
      {properties.ogImage && <meta property="og:image" content={properties.ogImage} />}
      {properties.ogImageAlt && <meta property="og:image:alt" content={properties.ogImageAlt} />}
      {properties.ogType && <meta property="og:type" content={properties.ogType} />}
      {properties.ogUrl && <meta property="og:url" content={properties.ogUrl} />}
      <meta property="og:title" content={properties.title} />
      <meta property="og:description" content={properties.metaDescription} />

      {/* Schema's */}
      {schema && <script {...jsonLdScriptProps<any>(schema)} />}

      {/* App Manifest */}
      {isZoocasaTenant && <link rel="manifest" href="/manifest.json" />}
      {/* Preload Fonts and images for Join eXp pages */}
      {isJoinExpPage || isExpCommercialHomepage && <link rel="preload" href="/fonts/manrope-extrabold.woff2" as="font" type="font/woff2" crossOrigin="anonymous" />}
      {isJoinExpPage &&
        <>
          <link key="j" rel="preload" href="assets/image/join-expus/join-letter-j.jpg" as="image" />
          <link key="o" rel="preload" href="assets/image/join-expus/join-letter-o.jpg" as="image" />
          <link key="i" rel="preload" href="assets/image/join-expus/join-letter-i.jpg" as="image" />
          <link key="n" rel="preload" href="assets/image/join-expus/join-letter-n.jpg" as="image" />
          <link key="u" rel="preload" href="assets/image/join-expus/join-letter-u.jpg" as="image" />
          <link key="s" rel="preload" href="assets/image/join-expus/join-letter-s.jpg" as="image" />
        </>
      }

      {/* Favicon based off active theme */}
      <link rel="icon" href={theme?.faviconPath} />

      {/* Language and Geo targeting */}
      {data?.hrefLangs?.map((hl, index) => <link rel="alternate" hrefLang={hl.hrefLang} href={hl.href} key={index} />)}
      {!!data?.hrefLangs?.length &&
        !data.hrefLangs.some(item => item.hrefLang === 'x-default') &&
        <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />}
    </Head>
  );
}